import { makeAutoObservable } from 'mobx'

import {
  KyrgyzstanSearchApi,
  type RealtyObject,
  type RealtySearchRequest,
} from 'shared/api/kyrgyzstan/search'
import { getApiErrorMessage } from 'shared/lib/errorText'

type DetailSearchParams = Omit<RealtySearchRequest, 'page' | 'page_size'>

class KyrgyzstanDetailDataStore {
  parentDetail: RealtyObject | null = null

  childrenList: RealtyObject[] = []

  isParentLoading = false

  isChildrenListLoading = false

  hasChildrenListLoaded = false

  parentError: string | null = null

  childrenListError: string | null = null

  private parentAbortController: AbortController | null = null

  private childrenListAbortController: AbortController | null = null

  constructor() {
    makeAutoObservable(this)
  }

  fetchParentDetail = async (parentId: string) => {
    this.parentAbortController?.abort()
    const abortController = new AbortController()
    this.parentAbortController = abortController
    this.isParentLoading = true
    this.parentError = null

    try {
      const { data: response } = await KyrgyzstanSearchApi.getRealtyDetail(
        parentId,
        abortController.signal,
      )

      if (this.parentAbortController === abortController) {
        this.parentDetail = response.data
      }
    } catch (error) {
      const errorMessage = getApiErrorMessage(error)

      if (this.parentAbortController === abortController && errorMessage) {
        this.parentError = errorMessage
        this.parentDetail = null
      }
    } finally {
      if (this.parentAbortController === abortController) {
        this.isParentLoading = false
        this.parentAbortController = null
      }
    }
  }

  fetchChildrenList = async (parentId: string, params?: DetailSearchParams) => {
    this.childrenListAbortController?.abort()
    const abortController = new AbortController()
    this.childrenListAbortController = abortController
    this.isChildrenListLoading = true
    this.hasChildrenListLoaded = false
    this.childrenListError = null

    try {
      const { data: response } = await KyrgyzstanSearchApi.getRealtyChildren(
        parentId,
        params,
        abortController.signal,
      )

      if (this.childrenListAbortController === abortController) {
        this.childrenList = response.data
        this.hasChildrenListLoaded = true
      }
    } catch (error) {
      const errorMessage = getApiErrorMessage(error)

      if (
        this.childrenListAbortController === abortController &&
        errorMessage !== null
      ) {
        this.childrenListError = errorMessage
        this.childrenList = []
      }
    } finally {
      if (this.childrenListAbortController === abortController) {
        this.isChildrenListLoading = false
        this.childrenListAbortController = null
      }
    }
  }

  resetParent = () => {
    this.parentAbortController?.abort()
    this.parentAbortController = null
    this.parentDetail = null
    this.isParentLoading = false
    this.parentError = null
  }

  resetChildrenList = () => {
    this.childrenListAbortController?.abort()
    this.childrenListAbortController = null
    this.childrenList = []
    this.isChildrenListLoading = false
    this.hasChildrenListLoaded = false
    this.childrenListError = null
  }

  reset = () => {
    this.resetParent()
    this.resetChildrenList()
  }
}

export const kyrgyzstanDetailDataStore = new KyrgyzstanDetailDataStore()
export type { DetailSearchParams }
